/**
 * @fileoverview Active organization context with deterministic bootstrap and storage sync.
 * @module features/orgs/context/ActiveOrgProvider
 */

/* eslint-disable react-refresh/only-export-components */ // This provider intentionally exports context helpers/constants alongside a component.
import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { getProfile, listOrgs } from '../api/orgsService'
import type { Org, OrgId } from '../schemas/orgSchemas'
import { supabase, getLastUserId, clearLastUserId } from '@/lib/supabaseClient'
import { queryClient } from '@/App'
import { devlog } from '@/shared/utils/devlog'
import { i18next } from '@/lib/i18n'

interface ActiveOrgContextValue {
  activeOrgId: OrgId | null
  setActiveOrgId: (id: OrgId | null) => void
  orgs: Org[]
  isLoading: boolean
  /** True once initial org bootstrap has completed (success or handled failure). */
  ready: boolean
  error?: string
  preferredLanguageMissing: boolean
  refreshFromProfile: (profile: {
    organizations: Org[]
    defaultOrgId?: OrgId | null
    preferredLanguage?: string
  }) => void
  markLanguageAsSet: () => void
}

const ActiveOrgContext = createContext<ActiveOrgContextValue | undefined>(undefined)

/** Storage key for active organization id. */
export const ACTIVE_ORG_KEY = 'activeOrgId'

/**
 * Provider that manages organizations and the active organization id.
 * Listens to Supabase auth events and loads orgs after INITIAL_SESSION.
 * - Validates persisted org id
 * - Syncs org selection across tabs via storage events
 *
 * Implementation note: We always bootstrap once on mount/INITIAL_SESSION regardless of route.
 */
export function ActiveOrgProvider({ children }: { children: React.ReactNode }): ReactElement {
  const [orgs, setOrgs] = useState<Org[]>([])
  const [activeOrgId, _setActiveOrgId] = useState<OrgId | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [preferredLanguageMissing, setPreferredLanguageMissing] = useState<boolean>(false)
  const inFlightRef = useRef(false)
  // Initialize to -Infinity so the first bootstrap call always passes the debounce check
  const lastBootstrapAtRef = useRef<number>(-Infinity)
  const hasBootstrappedRef = useRef<boolean>(false)

  const setActiveOrgId = useCallback((id: OrgId | null) => {
    _setActiveOrgId(id)
    try {
      if (id) localStorage.setItem(ACTIVE_ORG_KEY, id as unknown as string)
      else localStorage.removeItem(ACTIVE_ORG_KEY)
    } catch {
      // ignore storage errors
    }
  }, [])

  const bootstrapOrgs = useCallback(async () => {
    if (typeof window === 'undefined') return
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    console.log('[ActiveOrg] bootstrapOrgs called', {
      inFlight: inFlightRef.current,
      timeSinceLast: now - lastBootstrapAtRef.current,
      hasBootstrapped: hasBootstrappedRef.current,
    })
    if (inFlightRef.current) {
      console.log('[ActiveOrg] BLOCKED: already in flight')
      return
    }
    if (now - lastBootstrapAtRef.current < 800) {
      console.log('[ActiveOrg] BLOCKED: debounce (800ms)')
      return
    }
    inFlightRef.current = true
    setIsLoading(true)
    setError(undefined)
    try {
      devlog('ActiveOrg', 'bootstrap start')
      // Optimistic preselect from persisted value to unblock UI immediately
      try {
        const persistedPre = localStorage.getItem(ACTIVE_ORG_KEY) as OrgId | null
        if (persistedPre) {
          _setActiveOrgId(persistedPre)
          // Optimistically allow app render while we validate server-side
          setReady(true)
        }
      } catch {
        // ignore
      }
      const profile = await getProfile()
      setOrgs(profile.organizations)
      // Language hydrate
      if (profile.preferredLanguage) {
        void i18next.changeLanguage(profile.preferredLanguage)
        setPreferredLanguageMissing(false)
      } else {
        setPreferredLanguageMissing(true)
      }
      // Validate persisted selection against returned orgs
      let nextActive: OrgId | null = null
      try {
        const persisted = localStorage.getItem(ACTIVE_ORG_KEY) as OrgId | null
        if (
          persisted &&
          profile.organizations.some(
            o => (o.id as unknown as string) === (persisted as unknown as string)
          )
        ) {
          nextActive = persisted
        }
      } catch {
        // ignore
      }
      // Use backend default if provided
      if (!nextActive && profile.defaultOrgId) {
        nextActive = profile.defaultOrgId
      }
      // Do NOT auto-select first org when backend provided none
      setActiveOrgId(nextActive)
      devlog('ActiveOrg', 'profile loaded', {
        orgs: profile.organizations.length,
        selected: nextActive,
        hasLang: Boolean(profile.preferredLanguage),
      })
    } catch (e) {
      // Fallback: try listing orgs to at least populate selector
      try {
        const list = await listOrgs()
        setOrgs(list)
      } catch {
        // ignore secondary failure
      }
      setError((e as Error)?.message ?? 'Failed to load organizations')
      devlog('ActiveOrg', 'bootstrap error', { error: (e as Error)?.message })
    } finally {
      setIsLoading(false)
      lastBootstrapAtRef.current =
        typeof performance !== 'undefined' ? performance.now() : Date.now()
      inFlightRef.current = false
      hasBootstrappedRef.current = true
      setReady(true)
    }
  }, [setActiveOrgId])

  // Supabase auth: handle all session events
  // - INITIAL_SESSION: fires immediately when listener is set up (page load/refresh)
  // - SIGNED_IN: fires after successful login
  // - SIGNED_OUT: fires after logout
  useEffect(() => {
    console.log('[ActiveOrg] Setting up auth listener')
    // Listen for auth state changes
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[ActiveOrg] auth event received', { 
        event, 
        hasSession: Boolean(session),
        hasBootstrapped: hasBootstrappedRef.current,
        ready,
      })
      
      // INITIAL_SESSION fires on page load if session exists
      if (event === 'INITIAL_SESSION') {
        console.log('[ActiveOrg] INITIAL_SESSION handler', { 
          hasSession: Boolean(session),
          hasBootstrapped: hasBootstrappedRef.current 
        })
        if (session && !hasBootstrappedRef.current) {
          console.log('[ActiveOrg] Calling bootstrapOrgs from INITIAL_SESSION')
          void bootstrapOrgs()
        } else if (!session) {
          console.log('[ActiveOrg] No session, setting ready=true')
          // No session — mark ready so RequireOrg can redirect to login
          setReady(true)
        } else {
          console.log('[ActiveOrg] INITIAL_SESSION skipped: already bootstrapped')
        }
      }
      
      // When the user signs in, always bootstrap.
      // Reset debounce to ensure bootstrap runs even if a previous unauthenticated
      // attempt failed within the debounce window.
      if (event === 'SIGNED_IN') {
        console.log('[ActiveOrg] SIGNED_IN handler, resetting flags and bootstrapping')

        // Check if this is a different user than who logged out
        // If so, clear the query cache to prevent data leakage between users
        const lastUserId = getLastUserId()
        const currentUserId = session?.user?.id
        if (lastUserId && currentUserId && lastUserId !== currentUserId) {
          console.log('[ActiveOrg] Different user detected, clearing query cache', {
            lastUserId: lastUserId.slice(0, 8) + '...',
            currentUserId: currentUserId.slice(0, 8) + '...',
          })
          queryClient.clear()
        }
        // Clear the stored last user ID after comparison
        clearLastUserId()

        lastBootstrapAtRef.current = -Infinity
        hasBootstrappedRef.current = false
        void bootstrapOrgs()
      }
      
      if (event === 'SIGNED_OUT') {
        console.log('[ActiveOrg] SIGNED_OUT handler, clearing state')
        setOrgs([])
        setActiveOrgId(null)
        hasBootstrappedRef.current = false
        setReady(false)
      }
    })
    return () => {
      console.log('[ActiveOrg] Cleaning up auth listener')
      data?.subscription?.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ready excluded to avoid re-subscribing when auth state changes
  }, [bootstrapOrgs, setActiveOrgId])

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.key === ACTIVE_ORG_KEY) {
        _setActiveOrgId((e.newValue as OrgId | null) ?? null)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const refreshFromProfile = useCallback(
    (profile: {
      organizations: Org[]
      defaultOrgId?: OrgId | null
      preferredLanguage?: string
    }) => {
      setOrgs(profile.organizations)
      if (profile.preferredLanguage) {
        void i18next.changeLanguage(profile.preferredLanguage)
        setPreferredLanguageMissing(false)
      }
      setActiveOrgId(profile.defaultOrgId ?? null)
    },
    [setActiveOrgId]
  )

  const value = useMemo<ActiveOrgContextValue>(() => {
    const base = {
      activeOrgId,
      setActiveOrgId,
      orgs,
      isLoading,
      ready,
      preferredLanguageMissing,
      refreshFromProfile,
      markLanguageAsSet: () => setPreferredLanguageMissing(false),
    }
    return error !== undefined ? { ...base, error } : (base as ActiveOrgContextValue)
  }, [
    activeOrgId,
    setActiveOrgId,
    orgs,
    isLoading,
    ready,
    error,
    preferredLanguageMissing,
    refreshFromProfile,
  ])

  return <ActiveOrgContext.Provider value={value}>{children}</ActiveOrgContext.Provider>
}

/**
 * Hook to access the active organization context.
 * @throws Error when used outside of provider
 */
export function useActiveOrg(): ActiveOrgContextValue {
  const ctx = useContext(ActiveOrgContext)
  if (!ctx) throw new Error('useActiveOrg must be used within ActiveOrgProvider')
  return ctx
}
